import { lazy, Suspense, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import RequirementsField, { RequirementsFieldProps } from '~/components/core/forms/form-field-with-named-requirement';
import { AlgoliaSearchObjectAddress } from '~/lib/types/algolia-types';
import { QuoteAddress, QuoteRequirementSection } from '~/lib/types/basket-types';
import { BasketSections } from '~/lib/types/enums';

const SearchAddressContactName = lazy(() => import('./algolia'));

type SearchAddressContactNameProps = {
  type: BasketSections.ORIGIN | BasketSections.DESTINATION;
  address: QuoteAddress;
  onChange: (address: QuoteAddress) => void;
  loading: boolean;
  requirements: QuoteRequirementSection;
  errors: Record<string, string>;
  onAutoComplete: (address: AlgoliaSearchObjectAddress) => void;
  disableSearch: boolean;
  disabled: boolean;
};

export default function AddressContactName(props: SearchAddressContactNameProps) {
  const { isAuthenticated } = useAuth();

  const fieldProps = useMemo(() => {
    const p: RequirementsFieldProps = {
      requirements: props.requirements,
      name: 'contactname',
      id: `txt-${props.type}-name`,
      translationKey: `ADDRESS_FIELD_${props.type}_CONTACTNAME`.toUpperCase(),
      defaultLabel: 'Contact Name',
      data: props.address.contactname ?? '',
      update: (value) => props.onChange({ ...props.address, contactname: value }),
      errors: props.errors,
      loading: props.loading,
      autoComplete: props.type === BasketSections.ORIGIN ? 'name' : 'off',
      disabled: props.disabled
    };
    return p;
  }, [props]);

  return (
    <>
      {isAuthenticated && !props.disableSearch ? (
        <Suspense>
          <SearchAddressContactName
            {...fieldProps}
            country={props.address.country}
            onSelect={(p) => props.onAutoComplete({ ...props.address, ...p })}
            type={props.type}
            fallback={<RequirementsField {...fieldProps} />}
          />
        </Suspense>
      ) : (
        <RequirementsField {...fieldProps} />
      )}
    </>
  );
}
